// What actually decides whether a sound is heard.
//
// Everything below the port is arithmetic and everything above it is the game;
// this is the policy in between, and all of it is about **not** playing things.
// A turn moves an army, ticks every living side's machines and lands a dozen
// ingots, so the honest default is that most of what the board asks for is
// dropped: dropped before the first gesture, dropped inside another copy of
// itself, dropped past the voice cap, dropped when the file would not load.
// None of those is an error and none of them says anything — a game with no
// sound is a game, and a console error fails `board-smoke`.
//
// The port is the seam, so all of that is testable with no browser.

import { type Mix, clampVolume, sliderGain } from "./mix";
import { AMBIENCE, MusicPlayer, type MusicRegistry, TRACKS } from "./music";
import type { AudioPort } from "./port";
import { type Bus, type SoundId, SOUNDS, soundUrl } from "./sounds";
import { WebAudioPort } from "./webAudioPort";

/**
 * What the engine needs to know about the volume settings.
 *
 * **Structural, never imported.** `src/client/ai/audio/` depends on nothing, and the
 * settings belong to `src/client/ai/ui/` where the slider is — so the shape crosses and
 * the module does not.
 */
export interface SoundPrefs {
  master: number;
  music: number;
  ambience: number;
  sfx: number;
  ui: number;
  muted: boolean;
}

/**
 * What a level means when the stored one is unreadable.
 *
 * Full, because that is what having no preference at all means — and because
 * a `NaN` reaching an `AudioParam` does not fall back to anything, it poisons
 * the node and silences everything hanging off it.
 */
const DEFAULT_LEVEL = 1;

/**
 * How a sound is played this once, on top of what the manifest says.
 *
 * The manifest is one row per file, and a file heard at two moments of very
 * different weight — a piece being forged, the same piece being clicked — is
 * still one file. So the moment's weight is a caller's parameter and not a
 * second row: `gain` multiplies the manifest's, `rate` multiplies the base
 * playback rate before the jitter, and both default to leaving it alone.
 */
export interface Shape {
  gain?: number;
  rate?: number;
}

export interface PlayOptions {
  shape?: Shape;
}

export class SoundEngine {
  /**
   * Made on first use, never in the constructor.
   *
   * `sound` below is a module-scope instance, so a port built eagerly would
   * run in every importer — including a node test that pulled in a UI module
   * for something else entirely.
   */
  private port: AudioPort | null;

  /**
   * Decoded buffers, and `null` for a file that would not load.
   *
   * Keyed by *file* and not by id, because an id with takes is several files:
   * keyed by id, the first take to load would be cached as the whole sound and
   * the other three never heard.
   */
  private buffers = new Map<string, unknown | null>();
  /** Loads in flight, so a burst of plays makes one fetch. Keyed like above. */
  private loading = new Map<string, Promise<unknown | null>>();
  /**
   * The take each id sounded last, so the next one is a different file.
   *
   * Uniform picking repeats about a quarter of the time over four takes, and a
   * repeat inside a sift is exactly what the takes exist to remove.
   */
  private lastTake = new Map<SoundId, string>();
  /** Port-clock seconds of the last accepted play, per id. A fan is one
   * gesture whichever take sounds, so the cooldown is per id and not per file. */
  private lastPlay = new Map<SoundId, number>();
  /** Instances sounding right now, per id. Counted down by `onEnded`. */
  private live = new Map<SoundId, number>();

  /** Built on first use, like the port, and for the same reason. */
  private beds = new Map<Bus, MusicPlayer>();

  constructor(port?: AudioPort) {
    this.port = port ?? null;
  }

  /**
   * The music bed. One per page, sharing this engine's port and its bus.
   *
   * A getter rather than a field because the port is made on first use: a
   * player built in the constructor would either force a port into existence
   * in every importer or hold a second one, and a second graph is one nothing
   * ever unlocks.
   */
  get music(): MusicPlayer {
    return this.bed("music", TRACKS);
  }

  /** The ambience bed — the same player, a second registry, its own slider. */
  get ambience(): MusicPlayer {
    return this.bed("ambience", AMBIENCE);
  }

  unlock(): void {
    this.audio().unlock();
    // The beds are told directly rather than through an exported hook: this is
    // the one place that knows the browser has let go, and a bed asked for
    // during the menu has been waiting for exactly this. Iterating the map
    // rather than touching the getters, so unlocking cannot build a player
    // nobody has asked for.
    for (const bed of this.beds.values()) bed.onUnlocked();
  }

  /**
   * Push the volume settings into the graph.
   *
   * Mute is a master of nought rather than a flag anywhere in here, so the
   * stored level is untouched and unmuting is the same call with the same
   * number — which is what makes a mute button unable to lose somebody's
   * setting.
   */
  applyPrefs(prefs: SoundPrefs): void {
    const port = this.audio();
    const level = (v: number) => sliderGain(clampVolume(v, DEFAULT_LEVEL));
    port.setBusGain("master", prefs.muted ? 0 : level(prefs.master));
    port.setBusGain("music", level(prefs.music));
    port.setBusGain("ambience", level(prefs.ambience));
    port.setBusGain("sfx", level(prefs.sfx));
    port.setBusGain("ui", level(prefs.ui));
  }

  /** Play a sound on the board, placed by `mix` if the caller has a camera. */
  play(id: SoundId, opts: PlayOptions & { mix?: Mix; delaySec?: number } = {}): void {
    this.start(id, opts.mix, opts.delaySec, undefined, opts.shape);
  }

  /**
   * Play a sound as chrome: on the `ui` bus, in the middle, at full distance.
   *
   * The bus is overridden rather than being a second manifest entry, because
   * what changes between a forge on the board and the same file under a button
   * is not the sound — it is which slider should be able to silence it.
   */
  playUi(id: SoundId, opts: PlayOptions = {}): void {
    this.start(id, undefined, undefined, "ui", opts.shape);
  }

  private audio(): AudioPort {
    if (!this.port) this.port = new WebAudioPort();
    return this.port;
  }

  private bed(bus: Bus, tracks: MusicRegistry): MusicPlayer {
    let player = this.beds.get(bus);
    if (!player) {
      // The port is handed over as a thunk, so the bed reaches whichever port
      // this engine ends up with however late it is made.
      player = new MusicPlayer(() => this.audio(), bus, tracks);
      this.beds.set(bus, player);
    }
    return player;
  }

  private start(
    id: SoundId,
    mix: Mix | undefined,
    delaySec: number | undefined,
    bus: Bus | undefined,
    shape: Shape | undefined
  ): void {
    const spec = SOUNDS[id];
    if (!spec) return;

    const port = this.audio();
    // Before the first gesture there is nothing to play into. Not an error:
    // it is a sound nobody was allowed to hear.
    if (!port.unlocked) return;

    const now = port.now();
    const last = this.lastPlay.get(id);
    if (last !== undefined && now - last < spec.cooldownMs / 1000) return;

    const live = this.live.get(id) ?? 0;
    if (live >= spec.voices) return;

    // Booked before the load, so a burst arriving while the first fetch is in
    // flight is thrown out by the cooldown rather than queued up behind it.
    this.lastPlay.set(id, now);
    this.live.set(id, live + 1);
    const ended = () => this.live.set(id, Math.max(0, (this.live.get(id) ?? 1) - 1));

    const file = this.takeFor(id);
    if (this.buffers.has(file)) {
      const buffer = this.buffers.get(file);
      // A load that failed once is cached as a failure and never retried. The
      // alternative is a fetch per play for the rest of the game against a
      // file that is not there.
      if (buffer == null) return void ended();
      this.emit(port, id, buffer, mix, delaySec, bus, shape, ended);
      return;
    }

    let pending = this.loading.get(file);
    if (!pending) {
      pending = port.load(soundUrl(file));
      this.loading.set(file, pending);
    }
    // The first play of each file fires when its load resolves — one fetch of
    // delay on the very first occurrence, against buffers of a few tens of
    // kilobytes. Everything after it is immediate.
    void pending.then((buffer) => {
      this.buffers.set(file, buffer ?? null);
      this.loading.delete(file);
      if (buffer == null) return void ended();
      this.emit(port, id, buffer, mix, delaySec, bus, shape, ended);
    });
  }

  /**
   * Which file this play of `id` is, never the one it was last time.
   *
   * A sound with no takes is its one file and this is a lookup. With takes it
   * is a uniform pick over the *others*, which is what keeps a sift sounding
   * like cards rather than like a file being retriggered — `Math.random` is
   * allowed here; the ban is on `src/engine/rules/` and `src/engine/ai/`, where a game has
   * to be derivable from its log.
   */
  private takeFor(id: SoundId): string {
    const spec = SOUNDS[id];
    const takes = spec.takes;
    if (!takes || takes.length === 0) return spec.file;
    const last = this.lastTake.get(id);
    const pool = [spec.file, ...takes].filter((file) => file !== last);
    const file = pool[Math.floor(Math.random() * pool.length)] ?? spec.file;
    this.lastTake.set(id, file);
    return file;
  }

  private emit(
    port: AudioPort,
    id: SoundId,
    buffer: unknown,
    mix: Mix | undefined,
    delaySec: number | undefined,
    bus: Bus | undefined,
    shape: Shape | undefined,
    onEnded: () => void
  ): void {
    const spec = SOUNDS[id];
    const jitter = spec.rateJitter ?? 0;
    port.play(buffer, {
      bus: bus ?? spec.bus,
      // The manifest says how loud the sound is; the board says how far away
      // it is; the caller says how much of an occasion it is. A caller with no
      // camera to hand gets the manifest alone.
      gain: spec.gain * (mix?.gain ?? 1) * (shape?.gain ?? 1),
      pan: mix?.pan ?? 0,
      rate: (shape?.rate ?? 1) * (1 + (Math.random() * 2 - 1) * jitter),
      delaySec,
      onEnded
    });
  }
}

/**
 * The page's one engine.
 *
 * A single instance because the browser gives a page one audio graph and the
 * cooldowns and voice caps are only meaningful against all of it — two engines
 * would each politely refuse their own second step while playing each other's.
 */
export const sound = new SoundEngine();

/** Engines already wired, so a double mount does not stack listeners. */
const installed = new WeakSet<SoundEngine>();

/**
 * Open the audio context on the first thing the player does.
 *
 * A browser refuses to make noise until a gesture, so this is the one place the
 * whole system starts. `capture: true` so nothing can swallow it first, `once`
 * so neither listener outlives its job, and both call the same idempotent
 * `unlock` — a click *and* a keypress on the same frame is ordinary and has to
 * be harmless. Starting a game needs a menu click either way, so the context is
 * always open before the first board sound.
 */
export function installUnlock(engine: SoundEngine): void {
  if (typeof window === "undefined") return;
  // React mounts, unmounts and mounts again in StrictMode; a boot effect
  // calling this twice must not leave two of each listener behind.
  if (installed.has(engine)) return;
  installed.add(engine);
  const wake = () => engine.unlock();
  window.addEventListener("pointerdown", wake, { once: true, capture: true });
  window.addEventListener("keydown", wake, { once: true, capture: true });
}
